import React from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Button,
  Image,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import {
  determineScaleEligibility,
  ScaleEligibility,
} from "@/utils/survey/scales";

import { BaseQuestion, ImageBulletListCard } from "../core";

const QUESTION_ID = "scaleOptIn";

declare module "@/contexts" {
  interface SurveyAnswers {
    scaleOptIn?: boolean;
  }
}

const ScaleOptIn: React.FC<QuestionComponentProps> = ({ onClickNext }) => {
  const { eligibilityInfo, selectedProgram } = useAppContext();
  const { t } = useTranslation();

  const isIncludedScale = eligibilityInfo?.scaleEligible === "INCLUDED_SCALE";

  const scaleContext = isIncludedScale ? "included" : undefined;

  const questionText = t("scaleOptIn.questionText", { context: scaleContext });
  const helperTextKey = isIncludedScale
    ? "scaleOptIn.questionHelperText_included"
    : "scaleOptIn.questionHelperText";
  const ctaText = t("scaleOptIn.ctaText.yes", { context: scaleContext });

  const bulletListItems = [
    {
      image: "/assets/img/scaleOptIn-chart.png",
      text: t("scaleOptIn.valueProps.item1"),
    },
    {
      image: "/assets/img/scaleOptIn-scale.png",
      text: t("scaleOptIn.valueProps.item2"),
    },
  ];

  const DEFERRED_SHIPPING_TEXT: Partial<Record<ProgramOffering, JSX.Element>> =
    {
      NMPBA: (
        <Text>
          <Trans i18nKey="scaleOptIn.deferredShippingText.nmpba" />
        </Text>
      ),
      MED: <Text>{t("scaleOptIn.deferredShippingText.med")}</Text>,
    };

  return (
    <BaseQuestion
      hideHeader
      onClickBack={() => undefined}
      questionId={QUESTION_ID}
      questionText={questionText}
    >
      {isIncludedScale ? (
        <Image src="/assets/img/scale-earning.png" alt="Scale and app" />
      ) : (
        <ImageBulletListCard items={bulletListItems} />
      )}
      <Text>
        <Trans i18nKey={helperTextKey} />
      </Text>
      {selectedProgram && DEFERRED_SHIPPING_TEXT[selectedProgram] ? (
        <span>{DEFERRED_SHIPPING_TEXT[selectedProgram]}</span>
      ) : null}
      <Stack spacing={Spacing[4]} pt={Spacing[2]}>
        <Button
          colorScheme="primary"
          onClick={() => {
            onClickNext(true);
          }}
          size="xl"
          type="submit"
        >
          {ctaText}
        </Button>
        {!isIncludedScale && (
          <Button
            onClick={() => {
              onClickNext(false);
            }}
            size="xl"
            variant="outline"
          >
            {t("scaleOptIn.ctaText.no")}
          </Button>
        )}
      </Stack>
    </BaseQuestion>
  );
};

export const Q_SCALE_OPT_IN: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: (state) =>
    determineScaleEligibility(state) === ScaleEligibility.OPT_IN,
  component: ScaleOptIn,
};

// Members reach this only after the scale page's eligibility check, so anything
// but Noomcoin (free, included, or Med) gets the opt-in.
export const Q_SCALE_OPT_IN_SCALE_SURVEY: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: (state) =>
    determineScaleEligibility(state) !== ScaleEligibility.NOOMCOIN,
  component: ScaleOptIn,
};

export const Q_RETRIAGE_SCALE_OPT_IN: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["MED", "NMPBA"],
  shouldShowQuestion: ({ selectedProgram }) =>
    selectedProgram === "MED" || selectedProgram === "NMPBA",
  component: ScaleOptIn,
};
